import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PopUp from "../PopUp";
import api from "../api/api";
import { AppContext } from "../../App";
import Table from "../ui/Table";
import PencilSquareIcon from "../../icons/PencilSquareIcon";
import TrashCanIcon from "../../icons/TrashCanIcon";

export default function ListProducts() {
	const {
		setConfirm,
		setLoading,
		setEditProductsPopupVisible,
		config,
		loadProducts,
		categories,
		products,
		setListProductsPopupVisible,
		loadCategories,
		selectedEditCategory,
		setSelectedEditCategory,
		loadMenu,
	} = useContext(AppContext);
	const [departments, setDepartments] = useState([]);
	const [orderedProducts, setOrderedProducts] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchRows, setSearchRows] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const searchRequestRef = useRef(0);

	useEffect(() => {
		loadDepartments();
		loadCategories();
		if (selectedEditCategory) loadProducts();
	}, []);

	const loadDepartments = async () => {
		const res = await api.postProtected("/departments/list/", {
			per_pag: 300,
			orderby: "value,name",
		});
		if (res.success == 1) setDepartments(res.rows || []);
	};

	useEffect(() => {
		if (!selectedEditCategory && categories.length > 0)
			setSelectedEditCategory(categories[0].id);
	}, [categories]);

	useEffect(() => {
		if (selectedEditCategory) loadProducts();
	}, [selectedEditCategory]);

	const refreshRowsAfterMutation = async () => {
		if (isSearchMode) await loadSearchRows(searchTerm);
		else await loadProducts();
	};

	const deleteElement = async (id) => {
		setConfirm({
			title: "Sei sicuro?",
			onConfirm: async () => {
				setLoading(true);
				const res = await api.postProtected("/products/delete/" + id + "/");
				setLoading(false);
				if (res.success == 0)
					setConfirm({
						title: "Errore",
						message: res.error,
					});
				else {
					await refreshRowsAfterMutation();
					if (typeof loadMenu === "function") await loadMenu();
				}
			},
		});
	};

	const setProductActive = async (product, value) => {
		setLoading(true);
		const res = await api.postProtected("/products/edit/" + product.id + "/", {
			active: value,
		});
		setLoading(false);
	if (res.success == 0)
		setConfirm({
			title: "Errore",
			message: res.error,
		});
	else {
		await refreshRowsAfterMutation();
		if (typeof loadMenu === "function") await loadMenu();
	}
};

	const selectedCategory = categories.find(
		(category) => Number(category.id) === Number(selectedEditCategory),
	);
	const sortedCategories = [...categories].sort((a, b) => {
		const aPos = Number.isFinite(Number(a?.position)) ? Number(a.position) : 999999;
		const bPos = Number.isFinite(Number(b?.position)) ? Number(b.position) : 999999;
		if (aPos !== bPos) return aPos - bPos;
		return String(a?.name || "").localeCompare(String(b?.name || ""), "it", {
			sensitivity: "base",
		});
	});
	const sortedProducts = [...products].sort((a, b) => {
		const aPos = Number.isFinite(Number(a?.position)) ? Number(a.position) : 999999;
		const bPos = Number.isFinite(Number(b?.position)) ? Number(b.position) : 999999;
		if (aPos !== bPos) return aPos - bPos;
		return String(a?.name || "").localeCompare(String(b?.name || ""), "it", {
			sensitivity: "base",
		});
	});

	useEffect(() => {
		setOrderedProducts(sortedProducts);
	}, [products]);

	const categoriesMap = useMemo(() => {
		const map = {};
		for (const category of categories) map[Number(category.id)] = category;
		return map;
	}, [categories]);

	const normalizedSearchTerm = searchTerm.trim().toLowerCase();
	const isSearchMode = normalizedSearchTerm.length > 0;

	const applySearchFilter = (rows, term) => {
		const query = String(term || "").trim().toLowerCase();
		if (!query) return rows;
		return rows.filter((product) => {
			const productName = String(product?.name || "").toLowerCase();
			const categoryName = String(
				categoriesMap[Number(product?.category_id)]?.name || ""
			).toLowerCase();
			return productName.includes(query) || categoryName.includes(query);
		});
	};

	const loadSearchRows = async (term) => {
		const requestId = searchRequestRef.current + 1;
		searchRequestRef.current = requestId;
		setSearchLoading(true);
		const res = await api.postProtected("/products/list/", {
			fields: ["id", "name", "active", "price", "department_id", "position", "category_id"],
			orderby: "name",
			per_pag: 1000,
		});
		if (searchRequestRef.current !== requestId) return;
		setSearchLoading(false);
		if (res.success == 1) {
			const filtered = applySearchFilter(res.rows || [], term).sort((a, b) =>
				String(a?.name || "").localeCompare(String(b?.name || ""), "it", {
					sensitivity: "base",
				})
			);
			setSearchRows(filtered);
		} else {
			setSearchRows([]);
		}
	};

	useEffect(() => {
		if (!isSearchMode) {
			searchRequestRef.current += 1;
			setSearchRows([]);
			setSearchLoading(false);
			return;
		}
		const timeout = setTimeout(() => {
			loadSearchRows(searchTerm);
		}, 220);
		return () => clearTimeout(timeout);
	}, [searchTerm, categories]);

	const productPositionsMap = useMemo(() => {
		const map = {};
		for (const product of products) map[Number(product.id)] = Number(product.position);
		return map;
	}, [products]);

	const departmentsMap = useMemo(() => {
		const map = {};
		for (const department of departments) map[department.id] = department;
		return map;
	}, [departments]);
	const visibleRows = isSearchMode ? searchRows : orderedProducts;
	const tableRows = visibleRows.map((product) => ({
		...product,
		department_name: departmentsMap[product.department_id]?.name || "-",
		category_name: categoriesMap[Number(product.category_id)]?.name || "-",
	}));

	const reorderRows = (rows, draggedRow, targetRow) => {
		const draggedId = Number(draggedRow?.id);
		const targetId = Number(targetRow?.id);
		if (!draggedId || !targetId || draggedId === targetId) return rows;
		const nextRows = [...rows];
		const fromIndex = nextRows.findIndex((row) => Number(row.id) === draggedId);
		const toIndex = nextRows.findIndex((row) => Number(row.id) === targetId);
		if (fromIndex < 0 || toIndex < 0) return rows;
		const [moved] = nextRows.splice(fromIndex, 1);
		nextRows.splice(toIndex, 0, moved);
		return nextRows;
	};

	const reorderProducts = async (draggedProduct, targetProduct) => {
		const reordered = reorderRows(orderedProducts, draggedProduct, targetProduct);
		if (reordered === orderedProducts) return;
		setOrderedProducts(reordered);
		try {
			const updates = reordered
				.map((product, index) => ({
					id: product.id,
					position: index + 1,
				}))
				.filter((update) => productPositionsMap[Number(update.id)] !== update.position);

			await Promise.all(
				updates.map((update) =>
					api.postProtected("/products/edit/" + update.id + "/", {
						position: update.position,
					})
				)
			);
			await loadProducts(false);
			if (typeof loadMenu === "function") await loadMenu();
		} catch {
			setOrderedProducts(sortedProducts);
			setConfirm({
				title: "Errore",
				message: "Errore durante il riordino dei prodotti",
			});
		}
	};

	return (
		<PopUp
			size="large"
			options={{
				title: "Prodotti",
				headerButtons: (
					<button
						className="popup-header-add-button"
						disabled={!selectedEditCategory}
						onClick={() => {
							if (!selectedEditCategory) return;
							setEditProductsPopupVisible(true);
						}}
					>
						Aggiungi Prodotto
					</button>
				),
				onClose: () => setListProductsPopupVisible(false),
			}}
		>
			<div className="inner-content">
				<div className="products-admin-layout">
					<div className="products-admin-left-column">
						<div className="products-admin-search">
							<input
								type="text"
								placeholder="Cerca prodotto"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
							{searchTerm && (
								<button type="button" onClick={() => setSearchTerm("")}>
									&times;
								</button>
							)}
						</div>
						<div className="products-admin-categories">
							<div className="products-admin-categories-title">Categorie</div>
							<div className="products-admin-categories-list">
								{sortedCategories.map((category) => (
									<button
										key={category.id}
										className={
											"products-admin-category-button" +
											(Number(category.id) === Number(selectedEditCategory) ? " active" : "")
										}
										onClick={() => setSelectedEditCategory(category.id)}
									>
										{category.name}
									</button>
								))}
							</div>
						</div>
					</div>
						<div className="products-admin-content">
							<div className="products-admin-content-header">
								<div className="products-admin-current-category">
									{isSearchMode
										? "Risultati ricerca"
										: selectedCategory
											? selectedCategory.name
											: "Nessuna categoria selezionata"}
								</div>
							</div>
							{isSearchMode && searchLoading && (
								<div className="admin-search-status mb-2">Ricerca in corso...</div>
							)}
							{isSearchMode && !searchLoading && tableRows.length === 0 && (
								<div className="admin-search-empty mb-2">nessun prodotto trovato</div>
							)}
							{!selectedEditCategory && (
								<div className="text-warning mb-2">
									Crea prima una categoria per aggiungere prodotti.
								</div>
							)}
							<div className="products-admin-table">
								<Table
									header={true}
									config={config}
									draggableRows={!isSearchMode}
									onRowReorder={reorderProducts}
									cols={[
										// Colonna dedicata al drag handle per non sovrapporre l'ID
										{ id: "drag", label: "", type: "drag", align: "center", width: 0.4 },
										{ id: "id", label: "ID", type: "id", align: "center", width: 0.9 },
										{ id: "name", label: "Nome", type: "text", align: "left", width: 2 },
										{
											id: "category_name",
											label: "Categoria",
											type: "text",
											align: "left",
											width: 1.5,
										},
										{
											id: "price",
											label: "Prezzo",
											type: "eur",
											align: "right",
											width: 1,
										},
										{
											id: "department_name",
											label: "Reparto IVA",
											type: "text",
											align: "left",
											width: 1.5,
										},
										{
											id: "active",
											label: "In Vendita",
											type: "toggle",
											onChange: (product, value) => setProductActive(product, value),
											align: "center",
											width: 1.1,
										},
										{
											id: "buttons",
											label: "",
											type: "buttons",
											align: "right",
											width: 0.9,
											buttons: [
												{
													icon: <PencilSquareIcon />,
													onClick: (product) => setEditProductsPopupVisible(product.id),
													visible: true,
												},
												{
													icon: <TrashCanIcon />,
													onClick: (product) => deleteElement(product.id),
													className: "button-2",
													visible: true,
												},
											],
										},
									]}
									rows={tableRows}
								/>
							</div>
						</div>
					</div>
				</div>
			</PopUp>
	);
}
